import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "Content", "dropdownContentRef", "menuWidth", "minMenuWidth", "dropdownStyles"],
  _excluded2 = ["animateOnMount", "animateOnToggle", "aria-expanded", "aria-labelledby", "aria-invalid", "arrowColor", "arrowSize", "autoPlacement", "Button", "buttonRef", "buttonSize", "buttonText", "buttonUse", "caretRenderer", "children", "closeOnOutsideClick", "closeOnTargetLeave", "Content", "hasSearchbox", "dropdownContentRef", "dropdownStyles", "icon", "id", "isDisplayOptimized", "locked", "menuWidth", "minMenuWidth", "multiline", "onChangePlacement", "onClick", "onFocusLeave", "onOpenChange", "onOpenStart", "onOpenComplete", "open", "placement", "popoverRef", "popoverProps", "readOnly", "shouldCloseOnClick", "_useNativeButton", "responsive", "theme", "buttonStyles"];
import { Component } from 'react';
import memoizeOne from 'react-utils/memoizeOne';
import { Button as ButtonComponent } from '../Button';
import { Clickable } from '../Clickable';
import { FloatingButtonGroupContext } from '../FloatingButtonGroup/FloatingButtonGroupContext';
import { callIfPossible } from '../../utils/functions';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import ShareButton from '../ShareButton/ShareButton';
import { Popover } from '../Popover';
import { cancelSchedulerCallback, requestSchedulerCallback } from '../../utils/Timers';
import { render as customRenderer } from '../../utils/customRenderer';
import { uniqueId } from '../../utils/uniqueId';
import { DropdownCaret, StyledCaret } from './DropdownCaret';
import styled from 'foundations-theming/utils';
import { withTheme } from 'styled-components';
import { AbstractDropdownProvider } from './AbstractDropdownProvider';
import { StyledLink } from '../Link/Link';
import { StyledContentWrapper } from '../Typeahead/TypeaheadResultsItem';
import { DropdownContext } from '../Dropdown/DropdownContext';
import { deepMerge } from '../../utils/deepMerge';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { StyledImage } from '../Image/Image';
import { StyledStatusDot } from '../StatusDot/StatusDot';
import { StyledBadge } from '../Badge/Badge';
import { StyledView } from '../../../components/View/View';
import { StyledLoadingButtonContent } from '../LoadingButton/LoadingButton';
import { getSizeTokens, isFormUse, isLinkUse, isTransparentUse, isUnstyledUse } from '../Button/Button.helpers';
import { StyledItem, StyledContentLabel } from '../Typeahead/TypeaheadResultsItem';
import { CSS_SPECIFICITY } from '../../constants/CSSSpecificity';
import { getFocusPseudoClass, getFocusStyleObject } from '../../utils/focus/focus.style';
import { commonErrorStyles } from '../Select/Select.styles';
import { toLonghandSize } from '../../utils/tShirtSizes/tShirtSizes';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MIN_MENU_WIDTH = 144;
const getArrowSize = buttonUse => {
  if (buttonUse.match(/link|transparent/)) {
    return 'medium';
  }
  return 'none';
};
const getPlacement = (placement, computedArrowSize) => {
  if (placement) {
    return placement;
  }
  // Center if we have an arrow, otherwise flush left against the target
  if (computedArrowSize !== 'none') {
    return 'bottom';
  }
  return 'bottom right';
};
const StyledDropdownButtonLabel = styled.span.withConfig({
  displayName: "AbstractDropdown__StyledDropdownButtonLabel",
  componentId: "vb80yc-0"
})(({
  $noIcon,
  theme
}) => {
  return {
    display: $noIcon ? 'block' : 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start !important',
    flexGrow: 1,
    marginInlineEnd: !$noIcon ? theme.space['100'] : theme.space['0'],
    minWidth: 0,
    textAlign: 'left',
    paddingBlock: theme.space['50'],
    marginBlock: `-${theme.space['50']}`,
    hyphens: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    // adds margin inline end to direct children of the label that are icons
    // seeing as we can't style Icon directly and we don't want to wrap the icon
    // in another element to style it
    '& > svg[data-icon-name]': {
      marginInlineEnd: theme.space['200']
    },
    /**
     * When StyledItem is present, ensure it doesn't grow beyond parent
     * container's boundaries otherwise truncation won't work.
     */
    [`& ${StyledItem}`]: {
      maxWidth: '100%',
      /**
       * We cannot enforce truncation on any of the parent containers
       * (e.g. StyledItem or StyledContentWrapper) because in some cases
       * we are rendering an image (e.g. avatar icon) with negative margin
       * and that would clip this image, so we target the content label instead.
       */
      [`& ${StyledContentLabel}`]: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }
    },
    /**
     * When StyledItem is absent, apply truncation styles directly to the container.
     */
    [`&:not(:has(${StyledImage}))`]: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    [`& ${StyledContentWrapper}`]: {
      '& > *:first-child:not(:has(svg[data-icon-name]))': {
        marginBlockStart: theme.space['0'],
        alignItems: 'center'
      },
      [`&:has(${StyledImage}):not(:has(svg[data-icon-name]))`]: {
        marginBlock: `-${theme.space['25']}`
      },
      '& > *:first-child:has(svg[data-icon-name])': {
        marginInlineEnd: theme.space['200']
      }
    }
  };
});
export const DropdownContent = styled.div.withConfig({
  displayName: "AbstractDropdown__DropdownContent",
  componentId: "vb80yc-1"
})(({
  $styleOverrides,
  $cssStyles,
  $dropdownCssStyles,
  theme
}) => {
  return deepMerge({
    borderRadius: 'inherit',
    [`& ${StyledStatusDot}`]: {
      marginInlineStart: theme.space['100'],
      marginInlineEnd: '6px' // no token for 6px
    },
    [`& ${StyledView}:has(${StyledStatusDot})`]: {
      marginInlineEnd: '6px' // no token for 6px
    },
    // Ensures LoadingButton is rendered the same as Button in Dropdown
    [`& ${StyledLoadingButtonContent}`]: {
      display: 'inline-flex',
      justifyContent: 'flex-start'
    },
    [CSS_SPECIFICITY.OVERRIDE]: Object.assign({}, $dropdownCssStyles, $cssStyles)
  }, $styleOverrides !== null && $styleOverrides !== void 0 ? $styleOverrides : {});
});
export const StyledButtonContents = styled.span.withConfig({
  displayName: "AbstractDropdown__StyledButtonContents",
  componentId: "vb80yc-2"
})(({
  $buttonSize,
  $buttonUse,
  $isDisplayOptimized,
  theme
}) => Object.assign({
  maxWidth: '100%',
  alignItems: 'center',
  display: $buttonUse && isLinkUse($buttonUse) ? 'inline-flex' : 'flex',
  width: $isDisplayOptimized || $buttonUse && isLinkUse($buttonUse) ? 'auto' : '100%'
}, $buttonSize !== 'xs' && $buttonSize !== 'extra-small' && $buttonSize !== 'small' && $buttonSize !== 'sm' && !($buttonUse && isLinkUse($buttonUse)) && {
  lineHeight: theme.typography['detail-200'].lineHeight,
  [`&:has(${StyledBadge})`]: {
    lineHeight: '22px' // no line height token for 22px
  }
}, {
  [`& [${StyledLink}]`]: {
    display: 'inline-flex'
  },
  [`& ${StyledCaret}`]: {
    marginInlineStart: theme.space['0']
  }
}));
const getStyle = (menuWidth, controlWidth) => ({
  width: menuWidth || controlWidth,
  minWidth: menuWidth === 'auto' ? controlWidth : undefined
});
const renderButtonLabel = (buttonText, Icon, readOnly) => {
  return /*#__PURE__*/_jsxs(StyledDropdownButtonLabel, {
    $noIcon: Icon === null || readOnly,
    children: [Icon && /*#__PURE__*/_jsx(Icon, {}), buttonText == null || buttonText === '' ? '\u200B' : buttonText]
  });
};
const getButtonStyleOverrides = ({
  buttonUse,
  isDisplayOptimized,
  hasSearchbox,
  ariaInvalid,
  theme,
  buttonStyles
}) => {
  // We want to preserve the override styles from display optimized FormControl variant
  if (isDisplayOptimized) {
    return {
      backgroundColor: theme.color['fill-transparent-default'],
      border: 'none',
      fontSize: theme.typography['body-100'].fontSize,
      minHeight: '30px',
      lineHeight: theme.typography['body-100'].lineHeight,
      paddingBlock: theme.space['200'],
      paddingInlineEnd: theme.space['0'],
      paddingInlineStart: theme.space['0'],
      textDecoration: 'none',
      '&:hover:not([aria-disabled="true"]), &:focus:not([aria-disabled="true"]), &:focus-visible:not([aria-disabled="true"]), &:active:not([aria-disabled="true"]), &[aria-disabled="true"]': {
        backgroundColor: theme.color['fill-transparent-default'],
        border: 'none',
        outline: 'none',
        textDecoration: 'none'
      },
      // This is a workaround to ensure the text is vertically centered
      '& > span': {
        display: 'inline-flex'
      }
    };
  }
  const baseStyles = {
    textAlign: 'left',
    '&[data-action="close"]': {
      padding: theme.space['0']
    },
    // duplicates readonly styles from UIComponents. !important is required to override the default styles
    '&[aria-readonly="true"]': {
      backgroundColor: `${theme.color['fill-transparent-default']} !important`,
      borderColor: `${theme.color['fill-transparent-default']} !important`,
      color: `${theme.color['text-core-default']} !important`,
      cursor: 'auto'
    }
  };
  return Object.assign({}, baseStyles, buttonUse && isLinkUse(buttonUse) && {
    textDecoration: 'none'
  }, !(buttonUse && isLinkUse(buttonUse)) && ariaInvalid && {
    '&[aria-invalid="true"]': Object.assign({}, commonErrorStyles(theme))
  }, !hasSearchbox && Object.assign({}, !isUnstyledUse(buttonUse) && {
    display: 'inline-block'
  }, {
    alignItems: 'center'
  }), {
    [getFocusPseudoClass()]: isFormUse(buttonUse) || buttonUse === 'unstyled' ? getFocusStyleObject({
      theme,
      unwrapped: true,
      addBorderRadius: isLinkUse(buttonUse) || buttonUse === 'unstyled'
    }) : {},
    // Only apply buttonStyles and cssStyles to buttons that have data-dropdown-open attribute
    '&&[data-dropdown-open]': Object.assign({}, buttonStyles)
  });
};
class AbstractDropdownCore extends Component {
  constructor(props) {
    super(props);
    this.state = {
      controlWidth: 0
    };
    this.measureControlWidth = () => {
      // Make the menu width match up with the anchor width
      const {
        buttonRef,
        open
      } = this.props;
      const controlEl = buttonRef.current;
      if (controlEl && open) {
        const controlWidth = this.computeControlWidth(controlEl);
        if (controlWidth !== this.state.controlWidth) {
          this.setState({
            controlWidth
          });
        }
      }
    };
    this.isFocusWithinDropdown = () => {
      const {
        buttonRef
      } = this.props;
      const activeElement = document.activeElement;
      const dropdownNode = document.getElementById(this._contentId);
      if (!activeElement) {
        return false;
      }
      if (activeElement === buttonRef.current) {
        return true;
      } else if (dropdownNode && dropdownNode.contains(activeElement)) {
        return true;
      }
      return false;
    };
    this.handleButtonClick = evt => {
      const {
        locked,
        open,
        onClick
      } = this.props;
      const scrollPos = window.scrollY;
      callIfPossible(onClick, evt);
      if (!locked) {
        const nextOpen = !open;
        this.setIsOpen(nextOpen);
      }
      requestAnimationFrame(() => {
        if (window.scrollY !== scrollPos) {
          window.scrollTo(0, scrollPos);
        }
      });
    };
    this.handleWindowResize = () => {
      cancelSchedulerCallback(this._resizeFrame);
      this._resizeFrame = requestSchedulerCallback(this.measureControlWidth);
    };
    this.handleKeyDown = evt => {
      // Handle keydown events from both the button and the dropdown
      const {
        onKeyDown,
        open
      } = this.props;
      const {
        key
      } = evt;

      // Check if the event is from the button
      const isFromButton = evt.currentTarget.tagName === 'BUTTON';
      if (!open && isFromButton && (key === 'Enter' || key === ' ')) {
        // Dropdown's handleKeyDown & AbstractDropdown's handleKeyDown conflict with each other
        // This handleKeyDown will open it but calling onKeyDown (Dropdown's handleKeyDown) will close it immediately after
        if (onKeyDown) {
          callIfPossible(onKeyDown, evt);
          return;
        } else {
          this.setIsOpen(true);
          evt.preventDefault();
          evt.stopPropagation();
        }
      } else if (open && key === 'Escape') {
        // Dropdown's handleKeyDown doesn't handle Escape key so we can safely call onKeyDown
        this.setIsOpen(false);
        evt.stopPropagation();
      }
      callIfPossible(onKeyDown, evt);
    };
    this.setIsOpen = nextOpen => {
      const {
        onOpenChange,
        readOnly
      } = this.props;
      if (!onOpenChange || readOnly) {
        return;
      }
      onOpenChange(syntheticEvent(nextOpen));
    };
    this.focusOnControl = () => {
      const {
        buttonRef
      } = this.props;
      const controlNode = buttonRef.current;
      if (!controlNode) return;
      const scrollPos = window.scrollY;
      controlNode.focus({
        preventScroll: true
      });
      if (window.scrollY !== scrollPos) {
        window.scrollTo(0, scrollPos);
      }
    };
    this.computeControlWidth = controlEl => {
      const {
        open
      } = this.props;
      if (!controlEl || !open) {
        return 0;
      }
      const exactWidth = controlEl.getBoundingClientRect().width;
      const roundedWidth = window.chrome ? Math.floor(exactWidth) : exactWidth;
      return roundedWidth - 2; // account for 1px border added by UIPopover
    };
    this.renderContent = () => {
      const _this$props = this.props,
        {
          children,
          Content,
          dropdownContentRef,
          menuWidth,
          minMenuWidth = MIN_MENU_WIDTH,
          dropdownStyles
        } = _this$props,
        rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
      const controlWidth = Math.max(this.state.controlWidth, minMenuWidth);
      const {
        tokenProps
      } = filterProps(rest);
      /* eslint-disable-next-line react-hooks/rules-of-hooks */
      const cssStyles = useMapTokenPropsToCSS(tokenProps);
      /* eslint-disable-next-line react-hooks/rules-of-hooks */
      const dropdownCssStyles = useMapTokenPropsToCSS(dropdownStyles || {});
      return /*#__PURE__*/_jsx(DropdownContext.Consumer, {
        children: ({
          dropdownStyleOverrides
        }) => /*#__PURE__*/_jsx(DropdownContent, {
          id: this._contentId,
          onKeyDown: this.handleKeyDown,
          ref: dropdownContentRef,
          role: "presentation",
          style: this._getStyle(menuWidth, controlWidth),
          $styleOverrides: dropdownStyleOverrides,
          $cssStyles: cssStyles,
          $dropdownCssStyles: dropdownCssStyles,
          children: Content ? /*#__PURE__*/_jsx(Content, {}) : children
        })
      });
    };
    this._buttonContentId = uniqueId('abstractdropdown-button-content-');
    this._contentId = uniqueId('abstractdropdown-content-');
    this._getStyle = memoizeOne(getStyle);
    this.shouldCloseOnClick = this.shouldCloseOnClick.bind(this);
  }
  componentDidMount() {
    const {
      open
    } = this.props;
    if (open) {
      this.measureControlWidth();
      window.addEventListener('resize', this.handleWindowResize);
    }
  }
  componentDidUpdate(prevProps, prevState) {
    const {
      open
    } = this.props;

    // ⚠️ This condition prevents infinite loops! ⚠️
    if (prevState.controlWidth === this.state.controlWidth) {
      this.measureControlWidth();
    }
    if (prevProps.open && !open && this.isFocusWithinDropdown()) {
      this.focusOnControl();
    }
    if (!prevProps.open && open) {
      window.addEventListener('resize', this.handleWindowResize);
    } else if (prevProps.open && !open) {
      window.removeEventListener('resize', this.handleWindowResize);
      cancelSchedulerCallback(this._resizeFrame);
    }
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowResize);
    cancelSchedulerCallback(this._resizeFrame);
  }
  shouldCloseOnClick(evt) {
    const {
      buttonRef,
      shouldCloseOnClick
    } = this.props;

    // If `shouldCloseOnClick` is provided and returns a non-null value, defer to it
    if (shouldCloseOnClick) {
      const shouldClose = shouldCloseOnClick(evt);
      if (shouldClose != null) {
        return shouldClose;
      }
    }
    const target = evt.target;
    const controlNode = buttonRef.current;
    if (!controlNode) return null;

    // Ignore clicks on any <label> that contain the button
    let parentEl = controlNode;
    while (parentEl.parentElement) {
      parentEl = parentEl.parentElement;
      if (parentEl.tagName === 'LABEL' && parentEl.contains(target)) {
        return false;
      }
    }

    // Ignore clicks on any <label> whose "for" attr points to the button
    const id = controlNode.id;
    if (id) {
      const labelsForControl = document.querySelectorAll(`label[for="${id}"]`);
      for (let i = 0; i < labelsForControl.length; i++) {
        if (labelsForControl[i].contains(target)) {
          return false;
        }
      }
    }
    return null;
  }
  render() {
    const _this$props2 = this.props,
      {
        animateOnMount,
        animateOnToggle,
        'aria-expanded': ariaExpanded,
        'aria-labelledby': ariaLabelledby,
        'aria-invalid': ariaInvalid = false,
        arrowColor,
        arrowSize,
        autoPlacement = 'dropdown',
        Button,
        buttonRef,
        buttonSize = 'medium',
        buttonText,
        buttonUse = 'secondary',
        caretRenderer = /*#__PURE__*/_jsx(DropdownCaret, {
          buttonUse: buttonUse,
          buttonSize: buttonSize
        }),
        closeOnOutsideClick = true,
        closeOnTargetLeave,
        hasSearchbox,
        icon: Icon,
        id,
        isDisplayOptimized,
        locked: __locked = false,
        minMenuWidth: __minMenuWidth = MIN_MENU_WIDTH,
        onFocusLeave,
        onOpenChange,
        onOpenStart,
        onOpenComplete,
        open = false,
        placement,
        popoverRef,
        popoverProps,
        readOnly = false,
        _useNativeButton,
        responsive,
        theme,
        buttonStyles
      } = _this$props2,
      rest = _objectWithoutPropertiesLoose(_this$props2, _excluded2);
    const {
      domProps,
      restProps,
      callbackProps
    } = filterProps(rest);
    const computedArrowSize = arrowSize || getArrowSize(buttonUse);
    const computedPlacement = getPlacement(placement, computedArrowSize);
    const renderedButtonLabel = renderButtonLabel(buttonText, Icon);
    const renderedCaret = customRenderer(caretRenderer, {
      use: buttonUse,
      buttonSize
    });
    const ButtonType = Button || (buttonUse === 'unstyled' ? Clickable : ButtonComponent);
    const buttonProps = buttonUse === 'unstyled' ? {} : {
      _useNativeButton,
      size: buttonSize,
      use: buttonUse,
      responsive: isDisplayOptimized ? false : responsive
    };
    const computedAriaExpanded = !!(ariaExpanded != null ? ariaExpanded : open) || undefined;
    const buttonStyleOverrides = getButtonStyleOverrides({
      buttonUse,
      isDisplayOptimized,
      hasSearchbox: hasSearchbox !== null && hasSearchbox !== void 0 ? hasSearchbox : false,
      ariaInvalid,
      theme: theme,
      buttonStyles: buttonStyles !== null && buttonStyles !== void 0 ? buttonStyles : {}
    });
    const buttonSizeTokens = getSizeTokens(toLonghandSize(buttonSize), buttonUse);

    // We need to subtract the padding block end from the offset so that the dropdown menu doesn't look too far from the transparent variant of Button.
    // For other variants of Button, we don't need to do this because the Button has a background color and there's no empty white space between the Button text and the dropdown menu.
    // SpaceType only accepts positive values while MarginType accepts negative values, hence the type casting.
    const offset = isTransparentUse(buttonUse) ? `-${buttonSizeTokens.paddingBlockEnd}` : '0';
    return /*#__PURE__*/_jsx(AbstractDropdownProvider, {
      buttonStyleOverrides: buttonStyleOverrides,
      children: /*#__PURE__*/_jsx(Popover, Object.assign({
        animateOnMount: animateOnMount,
        animateOnToggle: animateOnToggle,
        arrowColor: arrowColor,
        arrowSize: computedArrowSize,
        autoPlacement: typeof autoPlacement === 'string' ? true : autoPlacement,
        closeOnOutsideClick: closeOnOutsideClick,
        closeOnTargetLeave: closeOnTargetLeave,
        Content: this.renderContent,
        onFocusLeave: onFocusLeave,
        onOpenChange: onOpenChange,
        onOpenStart: onOpenStart,
        onOpenComplete: onOpenComplete,
        open: open,
        placement: computedPlacement,
        popoverRef: popoverRef,
        shouldCloseOnClick: this.shouldCloseOnClick,
        offset: offset
      }, popoverProps, {
        children: /*#__PURE__*/_jsx(ButtonType, Object.assign({
          "aria-expanded": computedAriaExpanded,
          "aria-haspopup": true,
          "aria-invalid": ariaInvalid,
          "aria-labelledby": ariaLabelledby ? `${ariaLabelledby} ${this._buttonContentId}` : this._buttonContentId,
          "aria-readonly": readOnly,
          "aria-owns": this._contentId,
          "data-dropdown": true,
          "data-dropdown-open": open,
          id: id,
          onClick: this.handleButtonClick,
          onKeyDown: this.handleKeyDown,
          buttonRef: buttonRef
        }, domProps, callbackProps, restProps, buttonProps, {
          children: /*#__PURE__*/_jsxs(StyledButtonContents, {
            $buttonSize: buttonSize,
            $buttonUse: buttonUse,
            $isDisplayOptimized: isDisplayOptimized,
            id: this._buttonContentId,
            children: [renderedButtonLabel, !readOnly && renderedCaret]
          })
        }))
      }))
    });
  }
}
AbstractDropdownCore.contextType = FloatingButtonGroupContext;
export const AbstractDropdown = withTheme(ShareButton(AbstractDropdownCore));
AbstractDropdown.displayName = 'AbstractDropdown';